/**
 * YourWorkTracker — the "Your work" section of /portfolio-kit's
 * authenticated workspace.
 *
 * Two render branches off the artifact count:
 *   - count === 0 → onboarding empty state per docs/PORTFOLIO_KIT_IA_AND_ROADMAP.md
 *     §5 Q6: encouraging hero copy, primary "Create your first artifact"
 *     CTA, and a three-item getting-started checklist. No "0/0 artifacts"
 *     framings — that's psychologically negative on day one.
 *   - count >= 1 → list of <ArtifactCard /> items with a "Create artifact"
 *     CTA in the section header.
 *
 * All mutations (create, update, delete) live in this orchestrator —
 * <ArtifactCard /> is purely presentational and dispatches `onEdit(artifact)`
 * / `onDelete(artifact)` callbacks up to here. That keeps the card reusable
 * (e.g., if we ever surface artifacts in a dashboard widget) and keeps the
 * data flow inspectable in one place.
 *
 * The dialogs (<ArtifactFormDialog /> for create+edit, <DeleteConfirmDialog />
 * for delete confirmation) are rendered at the bottom of this component
 * conditionally on local state booleans. Each mutation invalidates
 * ['portfolioArtifacts'] on success so the list re-fetches automatically.
 *
 * Spec reference: docs/PORTFOLIO_KIT_IA_AND_ROADMAP.md §8 Phase B2.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useNavigate } from "react-router-dom";

import {
  createPortfolioArtifact,
  deletePortfolioArtifact,
  listPortfolioArtifacts,
  listPortfolioPromptGenerations,
  PortfolioArtifact,
  PortfolioArtifactCreate,
  PortfolioArtifactUpdate,
  PromptGeneration,
  updatePortfolioArtifact,
} from "../../api/client";
import ArtifactCard from "./ArtifactCard";
import ArtifactFormDialog from "./ArtifactFormDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import WorkspaceStatsHero from "./WorkspaceStatsHero";
import { useAuth } from "../../hooks/useAuth";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// Three-item getting-started checklist shown in the empty state. The
// checklist is purely visual at v1 — each item links to an existing
// surface, but the items don't actually check themselves off as the
// user makes progress. v1.1 can wire real progress detection.
const GETTING_STARTED: {
  titleKey: string;
  descriptionKey: string;
  route: string;
}[] = [
  {
    titleKey: "YourWorkTracker.gettingStartedPickArchetypeTitle",
    descriptionKey: "YourWorkTracker.gettingStartedPickArchetypeDescription",
    route: "/portfolio-kit/archetype/rag-agent",
  },
  {
    titleKey: "YourWorkTracker.gettingStartedReadReadmeTitle",
    descriptionKey: "YourWorkTracker.gettingStartedReadReadmeDescription",
    route: "/portfolio-kit/readme-template",
  },
  {
    titleKey: "YourWorkTracker.gettingStartedCreateArtifactTitle",
    descriptionKey: "YourWorkTracker.gettingStartedCreateArtifactDescription",
    route: "", // empty — this item's affordance is the primary CTA, not a link
  },
];


/**
 * Props are intentionally minimal — the component manages its own data
 * fetch and mutations. The parent (WorkspaceVariant in
 * PortfolioKitHome.tsx) just renders <YourWorkTracker /> and gets out
 * of the way.
 */
export default function YourWorkTracker() {
  const theme = useTheme();
  const { t } = useTranslation("portfolio-kit");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // --- Data fetch --------------------------------------------------------
  const {
    data: artifacts,
    isLoading,
    isError,
    error,
  } = useQuery<PortfolioArtifact[], Error>({
    queryKey: ["portfolioArtifacts", "list"],
    queryFn: listPortfolioArtifacts,
  });

  // Generations carry a reverse link back to the artifact they were saved to
  // (artifact_id). We fetch them only once we know there's at least one
  // artifact to annotate. Failures/loading are non-fatal — the "View prompt"
  // links simply don't render, so this query has no error UI of its own.
  const { data: generations } = useQuery<PromptGeneration[], Error>({
    queryKey: ["portfolioPromptGenerations", "list"],
    queryFn: listPortfolioPromptGenerations,
    enabled: (artifacts?.length ?? 0) >= 1,
  });

  // artifact_id -> generation id, for artifacts that have a linked generation.
  // generations is newest-first; guard so the NEWEST generation wins when an
  // artifact has multiple linked generations (last .set() would otherwise win).
  const generationByArtifact = new Map<string, string>();
  for (const gen of generations ?? []) {
    if (gen.artifact_id && !generationByArtifact.has(gen.artifact_id)) {
      generationByArtifact.set(gen.artifact_id, gen.id);
    }
  }

  // --- Dialog state ------------------------------------------------------
  // formDialog covers BOTH create and edit modes; `editingArtifact` decides
  // which one. Null + open => create. Non-null + open => edit.
  // Closing the dialog clears editingArtifact so the next "Create" click
  // doesn't accidentally inherit stale state.
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingArtifact, setEditingArtifact] =
    useState<PortfolioArtifact | null>(null);
  const [deletingArtifact, setDeletingArtifact] =
    useState<PortfolioArtifact | null>(null);

  const openCreateDialog = () => {
    setEditingArtifact(null);
    setFormDialogOpen(true);
  };

  const openEditDialog = (artifact: PortfolioArtifact) => {
    setEditingArtifact(artifact);
    setFormDialogOpen(true);
  };

  const closeFormDialog = () => {
    setFormDialogOpen(false);
    // Defer clearing editingArtifact so the dialog's exit animation can
    // still read it. (Setting to null synchronously would briefly flash
    // the create-mode title during the close transition.)
    setTimeout(() => setEditingArtifact(null), 200);
  };

  const openDeleteDialog = (artifact: PortfolioArtifact) => {
    setDeletingArtifact(artifact);
  };

  const closeDeleteDialog = () => {
    setDeletingArtifact(null);
  };

  // --- Mutations ---------------------------------------------------------
  // All three mutations invalidate the same query key root so the list
  // re-fetches after every change. We use the mutateAsync flavor because
  // both dialogs (ArtifactFormDialog, DeleteConfirmDialog) expect their
  // submit handler to return a Promise so they can show a loading state
  // and surface errors inline.
  const createMutation = useMutation({
    mutationFn: (payload: PortfolioArtifactCreate) =>
      createPortfolioArtifact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolioArtifacts"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; payload: PortfolioArtifactUpdate }) =>
      updatePortfolioArtifact(vars.id, vars.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolioArtifacts"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePortfolioArtifact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolioArtifacts"] });
    },
  });

  // Dialog onSubmit handler: routes to the right mutation based on mode.
  // The dialog handles its own loading + error states off the returned
  // promise; we just propagate the rejection.
  const handleFormSubmit = async (
    payload: PortfolioArtifactCreate | PortfolioArtifactUpdate,
  ) => {
    if (editingArtifact) {
      await updateMutation.mutateAsync({
        id: editingArtifact.id,
        payload: payload as PortfolioArtifactUpdate,
      });
    } else {
      await createMutation.mutateAsync(payload as PortfolioArtifactCreate);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingArtifact) return;
    await deleteMutation.mutateAsync(deletingArtifact.id);
  };

  // --- Loading skeleton ---------------------------------------------------
  if (isLoading) {
    return (
      <Box sx={{ mb: 6 }}>
        <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  // --- Error state -------------------------------------------------------
  // Keep the error compact: this is a workspace surface, not the whole page.
  // The user can refresh to retry. Auth errors are handled globally by the
  // axios interceptor (token refresh) and surface here as a hard error
  // only if refresh itself failed.
  if (isError) {
    return (
      <Paper
        sx={{
          p: 3,
          mb: 6,
          borderLeft: `4px solid ${theme.palette.primary.light}`,
        }}
      >
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
          {t("YourWorkTracker.couldntLoadTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error.message || t("YourWorkTracker.loadErrorFallback")}
        </Typography>
      </Paper>
    );
  }

  const count = artifacts?.length ?? 0;

  // --- Populated state (count >= 1) --------------------------------------
  // Section header (heading + Create button) + a vertical list of
  // <ArtifactCard /> items. Cards dispatch onEdit/onDelete callbacks up
  // to this orchestrator, which opens the appropriate dialog. The list
  // is already ordered newest-first by the backend (created_at DESC).
  if (count >= 1) {
    return (
      <>
        <Box sx={{ mb: 6 }}>
          <WorkspaceStatsHero
            artifacts={artifacts!}
            displayName={user?.display_name ?? null}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              {t("YourWorkTracker.yourWorkHeading")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{
                backgroundColor: theme.palette.primary.main,
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.85) },
              }}
            >
              {t("YourWorkTracker.createArtifactButton")}
            </Button>
          </Stack>

          <Stack spacing={2}>
            {artifacts!.map((artifact) => {
              const generationId = generationByArtifact.get(artifact.id);
              return (
                <ArtifactCard
                  key={artifact.id}
                  artifact={artifact}
                  onEdit={openEditDialog}
                  onDelete={openDeleteDialog}
                  onViewPrompt={
                    generationId
                      ? () =>
                          navigate(
                            `/portfolio-kit/lovable-prompt?generation=${generationId}`,
                          )
                      : undefined
                  }
                />
              );
            })}
          </Stack>
        </Box>

        {/* Dialogs render at the document root via MUI portals; positioning
            them inside this component is fine — MUI hoists them automatically. */}
        <ArtifactFormDialog
          open={formDialogOpen}
          mode={editingArtifact ? "edit" : "create"}
          initialArtifact={editingArtifact ?? undefined}
          onClose={closeFormDialog}
          onSubmit={handleFormSubmit}
        />
        <DeleteConfirmDialog
          open={deletingArtifact !== null}
          itemName={deletingArtifact?.title ?? ""}
          onClose={closeDeleteDialog}
          onConfirm={handleDeleteConfirm}
        />
      </>
    );
  }

  // --- Empty state (count === 0) -----------------------------------------
  // Q6 design: encouraging onboarding card + 3-item getting-started checklist.
  // No "0/0 artifacts" numbers anywhere — first impression should invite,
  // not measure. The ArtifactFormDialog is rendered here too so the
  // "Create your first artifact" CTA can open it; DeleteConfirmDialog
  // isn't needed in this branch (nothing to delete yet) but rendering it
  // unconditionally keeps the JSX shape symmetric with the populated branch.
  return (
    <>
    <Box sx={{ mb: 6 }}>
      <Paper
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
            theme.palette.primary.main,
            0.02,
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 700,
            letterSpacing: "0.08em",
          }}
        >
          {t("YourWorkTracker.emptyStateOverline")}
        </Typography>
        <Typography
          variant="h4"
          sx={{ fontWeight: 500, mt: 1, mb: 1.5, lineHeight: 1.2 }}
        >
          {t("YourWorkTracker.emptyStateHeading")}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 640 }}
        >
          {t("YourWorkTracker.emptyStateBody")}
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "white",
            "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.85) },
            mb: 4,
          }}
        >
          {t("YourWorkTracker.createFirstArtifactButton")}
        </Button>

        {/* Getting-started checklist. Items are non-interactive in the
            sense that they don't track real progress — they're three
            recommended starting actions. The first two are links; the
            third item's affordance is the CTA above. */}
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: "text.primary",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontSize: "0.75rem",
            }}
          >
            {t("YourWorkTracker.gettingStartedHeading")}
          </Typography>
          <Stack spacing={1.5}>
            {GETTING_STARTED.map((item, idx) => {
              const isLink = item.route !== "";
              const itemTitle = t(item.titleKey);
              const itemDescription = t(item.descriptionKey);
              const Inner = (
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    transition: "background-color 0.15s",
                    cursor: isLink ? "pointer" : "default",
                    "&:hover": isLink
                      ? { backgroundColor: alpha(theme.palette.primary.main, 0.06) }
                      : {},
                  }}
                >
                  <RadioButtonUncheckedIcon
                    sx={{
                      color: alpha(theme.palette.primary.main, 0.5),
                      fontSize: 22,
                      mt: 0.3,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500 }}
                      >
                        {itemTitle}
                      </Typography>
                      {isLink && (
                        <ArrowForwardIcon
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      {itemDescription}
                    </Typography>
                  </Box>
                </Stack>
              );

              if (!isLink) {
                return <Box key={idx}>{Inner}</Box>;
              }
              return (
                <Box
                  key={idx}
                  onClick={() => navigate(item.route)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(item.route);
                    }
                  }}
                  role="link"
                  tabIndex={0}
                  sx={{
                    "&:focus-visible": {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2,
                      borderRadius: 1.5,
                    },
                  }}
                >
                  {Inner}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Paper>
    </Box>

    <ArtifactFormDialog
      open={formDialogOpen}
      mode={editingArtifact ? "edit" : "create"}
      initialArtifact={editingArtifact ?? undefined}
      onClose={closeFormDialog}
      onSubmit={handleFormSubmit}
    />
    <DeleteConfirmDialog
      open={deletingArtifact !== null}
      itemName={deletingArtifact?.title ?? ""}
      onClose={closeDeleteDialog}
      onConfirm={handleDeleteConfirm}
    />
    </>
  );
}
